import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Tilt from 'react-parallax-tilt';
import {
  BeakerIcon,
  CodeBracketIcon,
  UserGroupIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import NET from 'vanta/dist/vanta.net.min';
import SEO from '../components/SEO';

function TechnologyCard({ icon: Icon, title, description, index }) {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.2 }}
    >
      <Tilt
        tiltMaxAngleX={10}
        tiltMaxAngleY={10}
        scale={1.05}
        transitionSpeed={2000}
      >
        <div className="glass p-8 rounded-modern text-center hover:bg-white/5 transition-all duration-300 h-full min-h-[300px] flex flex-col items-center justify-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-[#2563EB] to-[#4F46E5] rounded-full w-16 h-16 flex items-center justify-center glow">
              <Icon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h3 className="text-2xl font-semibold mb-4 text-white text-center">{title}</h3>
          <p className="text-white/80 text-center text-lg">{description}</p>
        </div>
      </Tilt>
    </motion.div>
  );
}

function StatsCard({ number, label, index }) {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <div className="glass p-8 rounded-modern text-center hover:bg-white/5 transition-all duration-300">
        <div className="text-4xl font-bold text-white mb-2">{number}</div>
        <div className="text-white/80 text-lg">{label}</div>
      </div>
    </motion.div>
  );
}

function About() {
  const [vantaEffect, setVantaEffect] = React.useState(null);
  const vantaRef = React.useRef(null);

  useEffect(() => {
    if (!vantaEffect) {
      setVantaEffect(
        NET({
          el: vantaRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.00,
          minWidth: 200.00,
          scale: 1.00,
          scaleMobile: 1.00,
          color: 0x3b82f6,
          backgroundColor: 0x0a1930,
          points: 20.00,
          maxDistance: 15.00,
          spacing: 15.00,
        })
      );
    }
    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  const technologies = [
    {
      icon: BeakerIcon,
      title: "Advanced AI Models",
      description: "Powered by state-of-the-art machine learning algorithms for precise background removal"
    },
    {
      icon: CodeBracketIcon,
      title: "Modern Architecture",
      description: "Built with cutting-edge technologies ensuring scalability and performance"
    },
    {
      icon: UserGroupIcon,
      title: "User-Centric Design",
      description: "Intuitive interface designed for both beginners and professionals"
    },
    {
      icon: CpuChipIcon,
      title: "Optimized Processing",
      description: "Lightning-fast processing with efficient resource utilization"
    },
    {
      icon: CloudArrowUpIcon,
      title: "Cloud Integration",
      description: "Seamless cloud storage and processing capabilities"
    },
    {
      icon: ShieldCheckIcon,
      title: "Secure Platform",
      description: "Enterprise-grade security protecting your data and privacy"
    }
  ];

  const stats = [
    { number: "99.9%", label: "Accuracy Rate" },
    { number: "5M+", label: "Images Processed" },
    { number: "50K+", label: "Active Users" },
    { number: "4.9", label: "User Rating" }
  ];

  return (
    <>
      <SEO 
        title="About Remuvr | Leading AI Background Removal Technology"
        description="Discover how Remuvr's cutting-edge AI technology is revolutionizing image editing. Learn about our mission, technology stack, and commitment to providing the most accurate background removal tool. Trusted by professionals worldwide."
        url="https://remuvr.com/about"
        pageName="about"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-[#1E40AF] to-[#1E3A8A] text-white" ref={vantaRef}>
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">About Remuvr</h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Discover how Remuvr is revolutionizing image processing with cutting-edge AI technology
              and a commitment to user experience.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {technologies.map((tech, index) => (
              <TechnologyCard key={index} {...tech} index={index} />
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <StatsCard key={index} {...stat} index={index} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default About;
